import { onMounted, ref } from "vue";

import { api } from "../api/client";
import type { SocialLink } from "../api/types";

/**
 * The site settings, held once for the whole visit.
 *
 * Deliberately not useAsyncData. That composable models one page loading its
 * own data: it starts a request per component, it owns a loading state and an
 * error message, and it offers a retry button. All three are wrong here.
 *
 * The footer is on every page, so a per-component request would mean one call
 * to the CMS on every navigation for an answer that cannot have changed
 * meanwhile. The state therefore lives in this module rather than in a
 * component: the first footer that mounts starts the request, every later one
 * reads the same ref, and a navigation costs nothing.
 *
 * There is no loading state and no error state either, and that is the point
 * of the second half. A footer without icons is a correct footer - it is what
 * a visitor sees when the band has no accounts filled in - so a failed request
 * is not worth a message, and a placeholder blinking at the foot of every page
 * would be more disruptive than the missing row it stands in for.
 *
 * Nothing is written to localStorage or sessionStorage: the state is the
 * module's, so it lives exactly as long as the tab does. A stored copy would
 * outlive an editor's change in the CMS, and there is nothing here worth
 * keeping across visits to pay for that.
 */

/**
 * The accounts to link to, in the order the CMS sent them. Empty until the
 * answer is in, and empty for good if it never comes.
 */
const socialLinks = ref<SocialLink[]>([]);

/**
 * Whether the one request has been started. Set before awaiting, so two
 * footers mounting in the same tick cannot both get past it.
 *
 * A failed request is not retried. The whole answer is one optional row of
 * icons; asking again on the next navigation would put the CMS under a request
 * per page view to maybe fill it in later.
 */
let started = false;

async function load(): Promise<void> {
  if (started) {
    return;
  }
  started = true;

  try {
    socialLinks.value = (await api.settings()).socialLinks;
  } catch {
    // Nothing to show and nothing to say: the footer renders without icons,
    // which is a state it has anyway. Not even a console line - this is an
    // unreachable CMS, not a bug in the page.
  }
}

/**
 * The settings, and the request that fills them in on first use.
 *
 * onMounted rather than a call in setup(), so the prerender in node makes no
 * request: scripts/prerender.mjs renders every route through one node process,
 * and a request there would both freeze an editor's accounts into the HTML
 * files and share this module's state across all of them.
 */
export function useSettings() {
  onMounted(load);

  return { socialLinks };
}
